import { SubscriberSet } from "./SubscriberSet";
import { SearchCriteria } from "../../../pojo/SearchCriteria";
import { ContactSubscription } from "./ContactSubscription";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { Proto } from "../../../pojo/Proto";
import { DataAccess } from "../../../dataaccess/data.access";
import { Item } from "../../../pojo/Item";
import { S25QLConst } from "../../../modules/s25-ql/s25ql.const";
import type { Tokenizer } from "../../../modules/s25-ql/s25ql.tokenizer";
import { SearchCriteriaService } from "../search-criteria/search.criteria.service";
import { ContactService } from "../../contact.service";
import { S25Util } from "../../../util/s25-util";

export class SubscriberSetUtil {
    // ============================================================================
    // PUBLIC API METHODS
    // ============================================================================

    /**
     * Convert a SearchCriteria.Model to subscriber set filter parameters
     * Validates that nested queries are not used (not supported for contacts)
     */
    public static modelToSubscriberSetFilter(model: SearchCriteria.Model): ContactSubscription.FilterParams {
        // Validate no nested searches
        if (model.step) {
            const hasNestedSearch = model.step.some((step: SearchCriteria.Step) => {
                const stepTypeId = Number(step.step_type_id);

                // Skip validation for step types that legitimately use itemId/itemName
                if (
                    stepTypeId === SearchCriteria.StepType.Contact.Contacts ||
                    stepTypeId === SearchCriteria.StepType.Contact.SecurityGroup ||
                    isContactCustomAttributeStepType(stepTypeId)
                ) {
                    return false;
                }

                // Check if any other step references another search (itemId + itemName indicates nested search)
                return step.step_param?.some((param: SearchCriteria.StepParam) => {
                    return param && typeof param === "object" && "itemId" in param && "itemName" in param;
                });
            });

            if (hasNestedSearch) {
                throw new Error(
                    "Nested query searches are not supported for contacts. Please use direct search criteria only.",
                );
            }
        }

        return SubscriberSetUtil.getFilterFromStep(model.step || []);
    }

    /**
     * Convert a subscriber set to SearchCriteria.Model for editing in the search builder
     * This allows reuse of existing search UI components
     */
    public static async subscriberSetToModel(
        set: SubscriberSet.Set<ContactSubscription.FilterParams>,
    ): Promise<SearchCriteria.Model> {
        const model: SearchCriteria.Model = {
            query_id: set.id,
            query_method: "all",
            step: [],
        };

        if (!set.filters) {
            return model;
        }

        const filters = set.filters;

        // Map filter params to search steps
        // Each filter parameter becomes a step with step_param containing the filter data

        // General parameters
        if (filters.first_name || filters.last_name || filters.internal_id) {
            const stepParam: Partial<{
                first_name: string;
                last_name: string;
                internal_id: string;
            }> = {};
            if (filters.first_name) stepParam.first_name = filters.first_name;
            if (filters.last_name) stepParam.last_name = filters.last_name;
            if (filters.internal_id) stepParam.internal_id = filters.internal_id;

            model.step.push({
                step_type_id: SearchCriteria.StepType.Contact.Details,
                step_param: [stepParam],
            });
        }

        // Keyword
        if (filters.name) {
            model.step.push({
                step_type_id: SearchCriteria.StepType.Contact.Keyword,
                step_param: [{ itemName: filters.name }],
            });
        }

        // Email
        if (filters.email) {
            model.step.push({
                step_type_id: SearchCriteria.StepType.Contact.Email,
                step_param: [{ email: filters.email }],
            });
        }

        // Modified since
        if (filters.modified_since || filters.modified_since_duration) {
            const stepParam: Partial<{
                modified_since: Proto.ISODateString;
                modified_since_duration: string;
            }> = {};
            if (filters.modified_since) stepParam.modified_since = filters.modified_since;
            if (filters.modified_since_duration) stepParam.modified_since_duration = filters.modified_since_duration;

            model.step.push({
                step_type_id: SearchCriteria.StepType.Contact.ModifiedSince,
                step_param: [stepParam],
            });
        }

        // Favorites
        if (filters.favorite) {
            model.step.push({
                step_type_id: SearchCriteria.StepType.Contact.Favorites,
                step_param: [{ favorite: filters.favorite }],
            });
        }

        // Organization
        if (filters.organization_id) {
            model.step.push({
                step_type_id: SearchCriteria.StepType.Contact.Details,
                step_param: [{ organization_id: filters.organization_id }],
            });
        }

        // Contact ID (specific contacts)
        if (filters.contact_id) {
            // Convert "1+2+3+4+5" format to array of objects with itemId
            const contactIds = filters.contact_id.split("+");
            const [contacts, error] = await S25Util.Maybe(ContactService.getContactsMinimal(contactIds.map(Number)));
            let step_param: S25ItemI[];
            if (contacts) {
                step_param = contacts.map((contact) => ({
                    itemId: contact.contact_id.toString(),
                    itemName: contact.contact_name,
                }));
            } else {
                //we had trouble getting the contacts but the search likely still works with just ids.
                step_param = contactIds.map((id) => ({ itemId: id.trim() }));
            }

            model.step.push({
                step_type_id: SearchCriteria.StepType.Contact.Contacts,
                step_param: step_param,
            });
        }

        // Range (alphabetical range)
        if (filters.range_start || filters.range_end) {
            const stepParam: Partial<{
                range_start: string;
                range_end: string;
            }> = {};
            if (filters.range_start) stepParam.range_start = filters.range_start;
            if (filters.range_end) stepParam.range_end = filters.range_end;

            model.step.push({
                step_type_id: SearchCriteria.StepType.Contact.Details,
                step_param: [stepParam],
            });
        }

        // Custom attributes - convert filter format to modeller format
        // Need to look up attribute details (name, type) by ID
        if (filters.attribute_id || filters.not_attribute_id) {
            // Ensure IDs are numbers for consistent comparison
            const attrId = filters.attribute_id ? Number(filters.attribute_id) : null;
            const notAttrId = filters.not_attribute_id ? Number(filters.not_attribute_id) : null;
            const attributeIds = [attrId, notAttrId].filter((id): id is number => id != null);
            const attributeMap = await lookupCustomAttributeDetails(attributeIds);

            if (attrId != null) {
                const attr = attributeMap.get(attrId);
                const hasValue = filters.attribute_value != null && filters.attribute_value !== "";
                const relationshipName: Tokenizer.CustomAttributeOperator = hasValue ? "is equal to" : "exists";

                model.step.push(
                    createCustomAttributeStep(
                        Item.Ids.Contact,
                        attrId,
                        attr,
                        relationshipName,
                        filters.attribute_value,
                    ),
                );
            }

            if (notAttrId != null) {
                const attr = attributeMap.get(notAttrId);
                model.step.push(createCustomAttributeStep(Item.Ids.Contact, notAttrId, attr, "does not exist", ""));
            }
        }

        // Security group
        if (filters.group_id) {
            // Convert "1+2+3+4+5" format to array of objects with itemId
            const groupIds = filters.group_id.split("+");
            const step_param: S25ItemI[] = groupIds.map((id) => ({ itemId: id.trim() }));

            model.step.push({
                step_type_id: SearchCriteria.StepType.Contact.SecurityGroup,
                step_param: step_param,
            });
        }

        return model;
    }

    public static subscriberSetToUrl(set: SubscriberSet.Set<ContactSubscription.FilterParams>): string {
        return "?" + DataAccess.params(set?.filters ?? {});
    }

    /**
     * Convert search steps to filter parameters.
     */
    public static getFilterFromStep(steps: SearchCriteria.Step[]): ContactSubscription.FilterParams {
        const filter: ContactSubscription.FilterParams = {};
        steps.forEach((step: SearchCriteria.Step) => {
            const stepTypeId = Number(step.step_type_id);

            if (isContactCustomAttributeStepType(stepTypeId)) {
                handleCustomAttributesStep(step, filter);
            } else if (stepTypeId === SearchCriteria.StepType.Contact.Keyword) {
                handleKeywordStep(step, filter);
            } else if (stepTypeId === SearchCriteria.StepType.Contact.Contacts) {
                handleContactsStep(step, filter);
            } else if (stepTypeId === SearchCriteria.StepType.Contact.SecurityGroup) {
                handleSecurityGroupStep(step, filter);
            } else {
                handleDefaultStep(step, filter);
            }
        });
        return filter;
    }
}

// ============================================================================
// PRIVATE HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a step_type_id represents a Contact custom attribute step.
 * Custom attribute step_type_ids are calculated as: (itemType * 100) + relationship offset
 * For Contact (type 3), they range from 350-358 depending on the operator
 * (base 300 + relationship offset 50-58, e.g., 357 for "exists", 356 for "does not exist")
 */
function isContactCustomAttributeStepType(stepTypeId: number): boolean {
    return (
        stepTypeId >= SearchCriteria.StepType.Contact.CustomAttributes &&
        stepTypeId < SearchCriteria.StepType.Contact.CustomAttributes + 10
    );
}

/**
 * Calculate relationship_type_id for a custom attribute
 * @param itemType The item type (e.g., Item.Ids.Contact = 3)
 * @param relationshipName The relationship operator name
 * @returns The relationship_type_id
 */
function getRelationshipTypeId(itemType: number, relationshipName: Tokenizer.CustomAttributeOperator): number {
    return itemType * 100 + S25QLConst.customAttributeRelationshipIds[relationshipName];
}

function createCustomAttributeStep(
    itemType: Item.Id,
    attributeId: number,
    attributeDetails: { name: string; type: string } | undefined,
    relationshipName: Tokenizer.CustomAttributeOperator,
    attributeValue: string | number | undefined,
): SearchCriteria.Step {
    const relationshipTypeId = getRelationshipTypeId(itemType, relationshipName);
    const stepTypeId = getRelationshipTypeId(itemType, relationshipName);

    return {
        step_type_id: stepTypeId,
        step_type_name: "Custom Attributes", // Required for serializer to recognize this as a custom attribute
        step_param: [
            {
                cust_atrb_id: String(attributeId),
                cust_atrb_name: attributeDetails?.name || "",
                cust_atrb_type: attributeDetails?.type || "S",
                itemId: attributeValue || "",
                itemName: attributeDetails?.name || "",
                relationship_type_id: relationshipTypeId,
                relationship_type_name: relationshipName,
            },
        ],
    };
}

/**
 * Look up custom attribute details (name, type) by ID
 */
async function lookupCustomAttributeDetails(
    attributeIds: number[],
): Promise<Map<number, { name: string; type: string }>> {
    const result = new Map<number, { name: string; type: string }>();
    if (!attributeIds.length) return result;

    try {
        // Include inactive to catch all attributes

        const data = await SearchCriteriaService.getCustomAttributeItems(Item.Ids.Contact, true);
        for (const attr of data || []) {
            if (attributeIds.includes(attr.itemId)) {
                result.set(attr.itemId, {
                    name: attr.itemName,
                    type: String(attr.itemTypeId),
                });
            }
        }

        // For any attributes not found, create a placeholder with the ID
        // This handles system attributes or attributes that might not be in the standard list
        for (const id of data || []) {
            if (!result.has(id.itemId)) {
                result.set(id.itemId, {
                    name: `Attribute ${id}`,
                    type: "S",
                });
            }
        }
    } catch (e) {
        console.error("Failed to lookup custom attribute details", e);
        // Still create placeholders so serialization doesn't fail
        for (const id of attributeIds) {
            result.set(id, {
                name: `Attribute ${id}`,
                type: "S",
            });
        }
    }

    return result;
}

/**
 * Extract itemIds from step_param entries and join with "+"
 */
function extractItemIds(stepParams: SearchCriteria.StepParam[]): string {
    return stepParams
        .map((param: SearchCriteria.StepParam) => (param as S25ItemI).itemId)
        .filter((id: string | number | undefined): id is string | number => id !== undefined && id !== null)
        .join("+");
}

/**
 * Check if a key is a valid FilterParams property name
 */
function isValidFilterKey(key: string): key is keyof ContactSubscription.FilterParams {
    return ContactSubscription.VALID_FILTER_PARAM_KEYS.includes(key as keyof ContactSubscription.FilterParams);
}

/**
 * Handle Keyword step type - extracts keyword from itemName and maps to name parameter
 */
function handleKeywordStep(step: SearchCriteria.Step, filter: ContactSubscription.FilterParams): void {
    const stepParam = step.step_param?.[0];
    if (stepParam && typeof stepParam === "object" && "itemName" in stepParam && stepParam.itemName) {
        filter.name = String(stepParam.itemName);
    }
}

/**
 * Handle Contacts step type - extracts contact IDs from step_param entries
 */
function handleContactsStep(step: SearchCriteria.Step, filter: ContactSubscription.FilterParams): void {
    const contactIds = extractItemIds(step.step_param);
    if (contactIds) {
        filter.contact_id = contactIds;
    }
}

/**
 * Handle SecurityGroup step type - extracts group IDs from step_param entries
 */
function handleSecurityGroupStep(step: SearchCriteria.Step, filter: ContactSubscription.FilterParams): void {
    const groupIds = extractItemIds(step.step_param);
    if (groupIds) {
        filter.group_id = groupIds;
    }
}

function handleCustomAttributesStep(step: SearchCriteria.Step, filter: ContactSubscription.FilterParams): void {
    const stepParam = step.step_param[0];
    if (!stepParam || typeof stepParam !== "object") {
        return;
    }

    // Handle modeller format (cust_atrb_id, cust_atrb_name, relationship_type_name, itemId)
    if ("cust_atrb_id" in stepParam && stepParam.cust_atrb_id != null) {
        const attributeId = Number(stepParam.cust_atrb_id);
        const relationshipName = stepParam.relationship_type_name as string;

        // "does not exist" maps to not_attribute_id, everything else maps to attribute_id
        if (relationshipName === "does not exist") {
            if (isValidFilterKey("not_attribute_id")) {
                filter.not_attribute_id = attributeId;
            }
        } else {
            if (isValidFilterKey("attribute_id")) {
                filter.attribute_id = attributeId;
                // Include attribute_value for operators that need it (not "exists")
                if (
                    relationshipName !== "exists" &&
                    isValidFilterKey("attribute_value") &&
                    "itemId" in stepParam &&
                    stepParam.itemId != null &&
                    stepParam.itemId !== ""
                ) {
                    filter.attribute_value = String(stepParam.itemId);
                }
            }
        }
        return;
    }

    // Handle legacy format (attribute_id, attribute_value)
    if (isValidFilterKey("attribute_id") && "attribute_id" in stepParam && stepParam.attribute_id != null) {
        filter.attribute_id = stepParam.attribute_id as number;
        if (
            isValidFilterKey("attribute_value") &&
            "attribute_value" in stepParam &&
            stepParam.attribute_value != null &&
            stepParam.attribute_value !== ""
        ) {
            filter.attribute_value = stepParam.attribute_value as string;
        }
    }

    if (isValidFilterKey("not_attribute_id") && "not_attribute_id" in stepParam && stepParam.not_attribute_id != null) {
        filter.not_attribute_id = stepParam.not_attribute_id as number;
    }
}

/**
 * Handle default step types - extracts properties from step_param[0]
 */
function handleDefaultStep(step: SearchCriteria.Step, filter: ContactSubscription.FilterParams): void {
    const stepParam = step.step_param[0];
    if (!stepParam || typeof stepParam !== "object") {
        return;
    }

    for (const [key, value] of Object.entries(stepParam)) {
        if (isValidFilterKey(key)) {
            (filter as Record<string, any>)[key] = value;
        }
    }
}
